import {
  Controller,
  type Control,
  type FieldPath,
  type FieldValues,
  type ControllerRenderProps,
} from 'react-hook-form'
import { Label } from '@/shared/components/ui/label'
import { cn } from '@/shared/lib/utils'

interface FormFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>
  name: TName
  label: string
  description?: string
  required?: boolean
  className?: string
  children: (field: ControllerRenderProps<TFieldValues, TName>) => React.ReactNode
}

export function FormField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  description,
  required,
  className,
  children,
}: FormFieldProps<TFieldValues, TName>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <div className={cn('flex flex-col gap-1.5', className)}>
          <Label
            htmlFor={name}
            className={cn(
              'text-sm',
              fieldState.error && 'text-destructive',
            )}
          >
            {label}
            {required && <span className="ml-0.5 text-destructive">*</span>}
          </Label>
          {children(field)}
          {description && !fieldState.error && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
          {fieldState.error && (
            <p className="text-xs text-destructive" role="alert">
              {fieldState.error.message}
            </p>
          )}
        </div>
      )}
    />
  )
}
